import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ContextualGlobalSsoBanner} from '@github-ui/global-sso-banner/contextual'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type OptionConfig} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {useLocation} from '@github-ui/react-router'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {ScreenFullIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {type ReactNode, useCallback, useEffect} from 'react'
import {
  type EntryPointComponent,
  graphql,
  type PreloadedQuery,
  useLazyLoadQuery,
  usePreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
} from 'react-relay'

import {IssueSidePanel} from '../components/show/IssueSidePanel'
import type {SavedViewsQuery} from '../components/sidebar/__generated__/SavedViewsQuery.graphql'
import MobileNavigation from '../components/sidebar/MobileNavigation'
import {SavedViewsGraphqlQuery as customViews} from '../components/sidebar/SavedViews'
import {Sidebar} from '../components/sidebar/Sidebar'
import {LABELS} from '../constants/labels'
import {useSetCurrentViewId} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {useIssueSidePanelNavigation} from '../hooks/use-issue-side-panel-navigation'
import {useRouteInfo} from '../hooks/use-route-info'
import {IssueRepoNewChoosePage, IssueRepoNewPage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {ClientSideRelayDataGeneratorViewQuery} from './__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import type {
  IssueDashboardKnownViewPageQuery,
  IssueDashboardKnownViewPageQuery$variables,
} from './__generated__/IssueDashboardKnownViewPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'
import {currentViewQuery as currentView} from './ClientSideRelayDataGenerator'
import {IssuesListSlots} from './IssuesListSlots'
import {List} from './List'

// Stable module-scope reference so `memo(List)` is preserved across unrelated
// re-renders. The List supplies `header`; IssuesListSlots renders the
// landmark structure.
const renderListSlotted = (parts: {header: ReactNode; filterBar: ReactNode; results: ReactNode}) => (
  <IssuesListSlots {...parts} />
)

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query IssueDashboardKnownViewPageQuery(
    $query: String = "state:open archived:false assignee:@me sort:updated-desc"
    $first: Int = 25
    $labelPageSize: Int = 20
    $skip: Int = null
    $type: SearchType = ISSUE_ADVANCED
    $showIssueFieldPills: Boolean = false
  ) {
    ...ListQuery
      @arguments(
        query: $query
        first: $first
        labelPageSize: $labelPageSize
        skip: $skip
        fetchRepository: true
        type: $type
        showIssueFieldPills: $showIssueFieldPills
      )
  }
`

export const IssueDashboardKnownViewPage: EntryPointComponent<
  {
    pageQuery: IssueDashboardKnownViewPageQuery
    customViewsQuery: SavedViewsQuery
  },
  Record<string, never>
> = ({queries: {pageQuery, customViewsQuery}}) => {
  const {
    sidePanelItemIdentifier,
    setSidePanelItemIdentifier,
    sidePanelItemURL,
    onCloseSidePanel,
    onParentIssueActivate,
  } = useRouteInfo()
  const {queryRef: pageQueryRef, loadQuery: loadQuery} = useEntryPointsLoader(pageQuery, PageQuery)
  const {queryRef: customViewsRef} = useEntryPointsLoader(customViewsQuery, customViews)
  const setCurrentViewId = useSetCurrentViewId()

  const {pathname} = useLocation()
  const id = pathname.split('/').pop()

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueRepoNewPage.preload()
      IssueRepoNewChoosePage.preload()
    })
  }, [])

  const {handleListItemsChange, handleNavigateToNextItem, handleNavigateToPreviousItem} = useIssueSidePanelNavigation({
    sidePanelItemIdentifier,
    setSidePanelItemIdentifier,
  })

  useEffect(() => {
    setCurrentViewId(id ? id : VIEW_IDS.empty)
  }, [id, pageQuery, setCurrentViewId])

  const onSubIssueClick = useCallback(
    (subIssueItem: SubIssueSidePanelItem) => {
      const {owner, repo, number} = subIssueItem
      setSidePanelItemIdentifier({owner, repo, number, type: 'Issue'})
    },
    [setSidePanelItemIdentifier],
  )

  const onIssueNavigate = useCallback(
    (issueIdentifier: {owner: string; repo: string; number: number}) => {
      setSidePanelItemIdentifier({...issueIdentifier, type: 'Issue'})
    },
    [setSidePanelItemIdentifier],
  )

  if (!pageQueryRef) return null
  if (!customViewsRef) return null

  const config: Partial<OptionConfig> = {
    shouldSkipSetDocumentTitle: true,
    onClose: onCloseSidePanel,
    insideSidePanel: true,
    showRepositoryPill: true,
    tabbedTimeline: isFeatureEnabled('issues_react_timeline_tabs'),
    onSubIssueClick,
    onIssueNavigate,
    onParentIssueActivate,
    navigateBack: onCloseSidePanel,
    onNavigateToNextItem: handleNavigateToNextItem,
    onNavigateToPreviousItem: handleNavigateToPreviousItem,
    additionalHeaderActions: (
      <IconButton
        as="a"
        role="link"
        variant="invisible"
        icon={ScreenFullIcon}
        aria-label={LABELS.sidePanelTooltip}
        href={sidePanelItemURL}
      />
    ),
  }
  const issueViewerConfig: OptionConfig = Object.assign({}, ISSUE_VIEWER_DEFAULT_CONFIG, config)

  return (
    <>
      <IssueDashboardKnownViewPageContent
        id={id}
        pageQueryRef={pageQueryRef}
        loadQuery={loadQuery}
        savedViewsQueryRef={customViewsRef}
        onSidePanelNavigate={setSidePanelItemIdentifier}
        onListItemsChange={handleListItemsChange}
      />
      {sidePanelItemIdentifier && (
        <IssueSidePanel onClose={onCloseSidePanel} optionConfig={issueViewerConfig}>
          <IssueViewer itemIdentifier={sidePanelItemIdentifier} optionConfig={issueViewerConfig} />
        </IssueSidePanel>
      )}
    </>
  )
}

function IssueDashboardKnownViewPageContent({
  id,
  pageQueryRef,
  loadQuery,
  savedViewsQueryRef,
  onSidePanelNavigate,
  onListItemsChange,
}: {
  id?: string
  pageQueryRef: PreloadedQuery<IssueDashboardKnownViewPageQuery>
  savedViewsQueryRef: PreloadedQuery<SavedViewsQuery>
  loadQuery: (
    variables: IssueDashboardKnownViewPageQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions | undefined,
  ) => void
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
}) {
  const data = usePreloadedQuery<IssueDashboardKnownViewPageQuery>(PageQuery, pageQueryRef)
  const currentViewNode = useLazyLoadQuery<ClientSideRelayDataGeneratorViewQuery>(
    currentView,
    {id},
    {fetchPolicy: 'store-only'},
  )

  const filteredListLayoutEnabled = isFeatureEnabled('global_issues_filtered_list_layout')

  const list = currentViewNode.node ? (
    <List
      fetchedView={currentViewNode.node}
      search={data}
      fetchedRepository={null}
      loadSearchQuery={loadQuery}
      onSidePanelNavigate={onSidePanelNavigate}
      onListItemsChange={onListItemsChange}
      showSsoBanner={false}
      renderSlotted={filteredListLayoutEnabled ? renderListSlotted : undefined}
    />
  ) : undefined

  if (filteredListLayoutEnabled) {
    return (
      <AnalyticsWrapper category="Issues Dashboard">
        <ContextualGlobalSsoBanner />
        <FilteredListPageLayout>
          <FilteredListPageLayout.Sidebar aria-label={LABELS.viewSidebarPaneAriaLabel} dialogTitle="Issues">
            <Sidebar customViewsRef={savedViewsQueryRef} />
          </FilteredListPageLayout.Sidebar>
          <FilteredListPageLayout.Content as="div">{list}</FilteredListPageLayout.Content>
        </FilteredListPageLayout>
        <MobileNavigation customViewsRef={savedViewsQueryRef} />
      </AnalyticsWrapper>
    )
  }

  return (
    <AnalyticsWrapper category="Issues Dashboard">
      <ContextualGlobalSsoBanner />
      <ThreePanesLayout
        leftPaneWidth={'small'}
        leftPane={{
          element: <Sidebar customViewsRef={savedViewsQueryRef} isFullHeight />,
          ariaLabel: LABELS.viewSidebarPaneAriaLabel,
        }}
        middlePane={list}
      />
      <MobileNavigation customViewsRef={savedViewsQueryRef} />
    </AnalyticsWrapper>
  )
}
